import { forwardRef } from '@chakra-ui/react-v2';
import type { ComponentWithAs, HTMLChakraProps } from '@chakra-ui/react-v2';
import { chakra, useMultiStyleConfig } from '@chakra-ui/system';
import { cx } from '@chakra-ui/utils';

import type { PartSize, PartVariant } from './types';
import { PartProvider } from './util';

export type HighlightableConstituent = 'number' | 'filename' | 'rev';

export type PartProps = {
  number?: string | null;
  filename?: string | null;
  rev?: string | null;
  isItar?: boolean;
  highlight?:
    | null
    | string
    | Partial<{ [C in HighlightableConstituent]: string | null }>;
  truncate?: boolean;
} & HTMLChakraProps<'div'> & {
    size?: PartSize;
    /**
     * The variant of the Part.
     * The default variant is default.
     * The borderless variant is borderless.
     */
    variant?: PartVariant;
  };

/**
 * A Part is a single part.
 * It is a container for a PartThumbnail and PartLabel.
 */
export const Part: ComponentWithAs<'div', PartProps> = forwardRef<
  PartProps,
  'div'
>(
  (
    {
      number,
      filename,
      rev,
      isItar,
      highlight,
      truncate,
      size,
      variant,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const styles = useMultiStyleConfig('Part', { size, variant });
    const _className = cx('part', className);

    const truncateProps = truncate
      ? {
          minWidth: 0,
        }
      : {};

    return (
      <PartProvider
        value={{
          size,
          variant,
          number,
          filename,
          rev,
          isItar,
          highlight,
          truncate,
        }}
      >
        <chakra.div
          __css={styles.container}
          {...props}
          className={_className}
          ref={ref}
          {...truncateProps}
        >
          {children}
        </chakra.div>
      </PartProvider>
    );
  },
);
Part.displayName = 'Part';
